import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronRight, ChevronLeft, Quote } from 'lucide-react';

// Testimonianze VERE verificate da Google Reviews (sincronizzate con Homepage)
const testimonials = [
  {
    name: "Simonangelo Grasso",
    business: "Navigazione Aerea",
    text: "Grazie a Manuel e a tutto il suo team, ho finalmente potuto aumentare considerevolmente la mia visibilità con il mio progetto riguardante un sito web di navigazione aerea. Molto attenti alle esigenze del cliente e soprattutto abili, proattivi e veloci nel cercare una soluzione e consegnare il progetto nel più breve tempo possibile. Molto raccomandato!",
    rating: 5
  },
  {
    name: "Diana Piano",
    business: "Imprenditrice",
    text: "Professionalità, esperienza e velocità. Ci hanno aiutato tanto in termini di visibilità con la scheda google e sito web. Ragazzi onesti e disponibili. Consigliati!",
    rating: 5
  },
  {
    name: "Vanessa Laureana",
    business: "Ambulatorio Medico",
    text: "Mi sono rivolta a questa web agency per lo sviluppo di materiali di comunicazione ed aumentare la media delle recensioni con l'obiettivo di migliorare la visibilità locale del mio ambulatorio.",
    rating: 5
  },
  {
    name: "Luigi B.",
    business: "Coffee Pub",
    text: "Ottima esperienza, si sono occupati di tutta la visibilità del mio coffee pub: dal sito web ai bigliettini da visita e qrcode, nonché tutta la parte di Google.",
    rating: 5
  },
  {
    name: "Mattia Maron",
    business: "Artigiano",
    text: "Come artigiano, il mio lavoro parla da sé ma non sapevo come mostrarlo online. Ora ho un sito che riflette la mia immagine e la qualità delle mie creazioni. I clienti mi contattano già sapendo cosa aspettarsi.",
    rating: 5
  },
  {
    name: "Rebecca Cosentino",
    business: "Azienda",
    text: "Per noi è stata un'esperienza positiva e concreta. Li consigliamo a chi cerca un supporto serio per migliorare la propria presenza online.",
    rating: 5
  },
  {
    name: "Christian Vocca",
    business: "Imprenditore",
    text: "Avevo bisogno di un sito veloce e chiaro, che mi desse visibilità e mi permettesse di avvicinare un buon numero di clienti nuovi.",
    rating: 5
  }
];

const TestimonialsSection = ({ compact = false }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -350, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 350, behavior: 'smooth' });
    }
  };

  const MAX_LENGTH = 150;

  return (
    <section className={`${compact ? 'py-16' : 'py-24'} px-6 bg-[#0a0a0a] relative overflow-hidden`}>
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Testimonianze
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Cosa dicono{" "}
            <span className="bg-gradient-to-r from-sky-400 to-cyan-300 bg-clip-text text-transparent">
              i nostri clienti
            </span>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto">
            Recensioni verificate da Google. Risultati concreti per aziende e professionisti.
          </p>
        </motion.div>

        <div className="relative">
          {/* Scroll buttons */}
          <button 
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-sky-500 text-white flex items-center justify-center transition-colors hidden md:flex"
            style={{ marginLeft: '-20px' }}
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-sky-500 text-white flex items-center justify-center transition-colors hidden md:flex"
            style={{ marginRight: '-20px' }}
          >
            <ChevronRight size={20} />
          </button>

          {/* Testimonials carousel */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => {
              const isExpanded = expandedIndex === index;
              const needsTruncate = testimonial.text.length > MAX_LENGTH;
              const displayText = isExpanded || !needsTruncate 
                ? testimonial.text 
                : testimonial.text.substring(0, MAX_LENGTH) + '...';
              
              return (
                <motion.div 
                  key={index}
                  className="flex-shrink-0 w-[320px] md:w-[380px] bg-[#111] border border-white/10 rounded-2xl p-6 snap-start flex flex-col hover:border-sky-500/30 transition-all duration-300"
                  style={{ minHeight: '280px' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-[#0ea5e9] fill-[#0ea5e9]" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <div className="flex-grow">
                    <Quote size={24} className="text-sky-500/30 mb-2" />
                    <p className="text-white/70 text-sm leading-relaxed">
                      {displayText}
                    </p>
                    {needsTruncate && (
                      <button 
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className="text-[#0ea5e9] text-sm mt-2 hover:underline"
                      >
                        {isExpanded ? 'Mostra meno' : 'Leggi di più'}
                      </button>
                    )}
                  </div>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {testimonial.name.split(' ')[0][0]}{testimonial.name.split(' ')[1]?.[0] || ''}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{testimonial.name}</p>
                      <p className="text-white/40 text-xs">{testimonial.business}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
export { testimonials };
